/**
 * useLockedDnmStatus — Phase 2 voting hook
 *
 * Determines which variant of the locked-DNM status box to render.
 * Multicall reads voterStake, checkVotingPeriod, voterStakeCount.
 * Includes localStorage flag for cross-session Variant B detection (P2-01 r2).
 *
 * Variant logic:
 *   'active'    — voterStake > 0n AND voting period is over (AC-25, can withdraw)
 *   'deposited' — voterStake > 0n AND voting period is active (AC-24, locked)
 *   'withdrawn' — voterStake === 0n AND hasEverVoted (AC-26, already withdrew)
 *   'hidden'    — voterStake === 0n AND !hasEverVoted (AC-27, never voted via wallet-stake)
 *
 * AC-26 localStorage caveat: Option α — covers same browser across sessions.
 * Falls back to 'hidden' on data clear / different browser / storage unavailable.
 * Acknowledged partial per service-logic.md §2.2 P2-01 r2.
 *
 * staleTime intentionally omitted — requires per-call PM approval per 2026-05-05 rule.
 * IDocs spec value (30_000) preserved in service-logic.md if PM re-approves.
 *
 * See: arandao-docs/pipeline/DAO-FOUND-02/idocs/voting/service-logic.md §2.2
 */
import type { Address } from "viem";
import { useReadContracts } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { ELECTION_ABI } from "@/contract/abi/election";
import ContractAddress from "@/contract/addresses";

export type LockedDnmVariant = "active" | "withdrawn" | "deposited" | "hidden";

export interface LockedDnmStatus {
  variant: LockedDnmVariant;
  voterStake: bigint;
  isVotingPeriod: boolean;
  hasEverVoted: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

// ─── localStorage helpers (exported for direct testing per V-T-08b) ────────

/**
 * Reads whether the user withdrew their locked DNM for the given cycle.
 * Key shape: dao:withdrawn:<addr-lowercased>:<cycle>
 * SSR-safe: returns false when window is unavailable.
 */
export function hasWithdrawnLocalFlag(
  addr: Address | undefined,
  cycle: number | undefined,
): boolean {
  if (typeof window === "undefined" || !addr || cycle === undefined)
    return false;
  try {
    return (
      window.localStorage.getItem(
        `dao:withdrawn:${addr.toLowerCase()}:${cycle}`,
      ) === "1"
    );
  } catch {
    return false;
  }
}

/**
 * Writes the withdrawn flag for the given address+cycle.
 * Called by useWithdrawDnm.onSuccess BEFORE invalidating queries.
 * SSR-safe, quota-safe: silently swallows any storage error.
 */
export function setWithdrawnLocalFlag(
  addr: Address | undefined,
  cycle: number | undefined,
): void {
  if (typeof window === "undefined" || !addr || cycle === undefined) return;
  try {
    window.localStorage.setItem(
      `dao:withdrawn:${addr.toLowerCase()}:${cycle}`,
      "1",
    );
  } catch {
    // Storage unavailable / quota — fail silent. In-session Variant B still works via React state.
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────

const HIDDEN_STATE: LockedDnmStatus = {
  variant: "hidden",
  voterStake: 0n,
  isVotingPeriod: false,
  hasEverVoted: false,
  isLoading: false,
  isError: false,
  refetch: () => undefined,
};

export function useLockedDnmStatus(
  userAddress: Address | undefined,
  cycle: number | undefined,
): LockedDnmStatus {
  const enabled = userAddress !== undefined && cycle !== undefined;

  const { data, isLoading, isError, refetch } = useReadContracts({
    contracts: [
      {
        address: ContractAddress.ELECTION,
        abi: ELECTION_ABI,
        chainId: EXPECTED_CHAIN_ID,
        functionName: "voterStake",
        args: [userAddress ?? "0x0000000000000000000000000000000000000000"],
      },
      {
        address: ContractAddress.ELECTION,
        abi: ELECTION_ABI,
        chainId: EXPECTED_CHAIN_ID,
        functionName: "checkVotingPeriod",
      },
      {
        address: ContractAddress.ELECTION,
        abi: ELECTION_ABI,
        chainId: EXPECTED_CHAIN_ID,
        functionName: "voterStakeCount",
        args: [
          userAddress ?? "0x0000000000000000000000000000000000000000",
          BigInt(cycle ?? 0),
        ],
      },
    ],
    query: {
      enabled,
      retry: false,
    },
  });

  if (!enabled) {
    return HIDDEN_STATE;
  }

  if (isLoading) {
    return { ...HIDDEN_STATE, isLoading: true };
  }

  const voterStake =
    data?.[0]?.status === "success" && typeof data[0].result === "bigint"
      ? data[0].result
      : 0n;

  const isVotingPeriod =
    data?.[1]?.status === "success" && typeof data[1].result === "boolean"
      ? data[1].result
      : false;

  const voterStakeCount =
    data?.[2]?.status === "success" && typeof data[2].result === "bigint"
      ? data[2].result
      : 0n;

  // P2-01 r2 — localStorage flag for cross-session Variant B detection
  const withdrawnFlag = hasWithdrawnLocalFlag(userAddress, cycle);

  // hasEverVoted: true if any of the three signals indicate prior wallet-stake vote
  const hasEverVoted = voterStake > 0n || voterStakeCount > 0n || withdrawnFlag;

  // Compute variant per service-logic.md §2.2 trigger table
  let variant: LockedDnmVariant;
  if (voterStake > 0n && !isVotingPeriod) {
    variant = "active"; // AC-25 — Variant A, can withdraw
  } else if (voterStake > 0n && isVotingPeriod) {
    variant = "deposited"; // AC-24 — Variant C, locked during voting period
  } else if (voterStake === 0n && hasEverVoted) {
    variant = "withdrawn"; // AC-26 — Variant B, already withdrew
  } else {
    variant = "hidden"; // AC-27 — never voted via wallet-stake, section not shown
  }

  return {
    variant,
    voterStake,
    isVotingPeriod,
    hasEverVoted,
    isLoading: false,
    isError,
    refetch,
  };
}
